import React, { useState } from "react";
import {
  FaShieldAlt,
  FaEye,
  FaEyeSlash,
  FaArrowRight,
  FaArrowLeft,
  FaCheckCircle,
  FaSpinner,
} from "react-icons/fa";
import "../../../assets/css/General/resetpassword.css";
import logo from "../../../assets/images/logo.png";


/**
 * Reusable "Create New Password" screen for both Buyer & Seller flows.
 * Same pattern as OtpVerification: the parent wrapper page
 * (BuyerResetPassword / SellerResetPassword) owns the API call and
 * passes handleReset + loading down as props.
 *
 * Props:
 *  - email       : string   -> shown for context (optional)
 *  - userType    : "buyer" | "seller"
 *  - handleReset : fn(password, confirmPassword) -> called on submit
 *  - loading     : bool
 *  - loginPath   : string   -> route for "Back to Login"
 *  - success     : bool     -> parent flips this to show the success state
 */
function ResetPassword({
  email = "",
  userType = "buyer",
  handleReset,
  loading = false,
  loginPath = "/login",
  success = false,
}) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState({});

  /* ------------- Strength (3 bars: Weak / Medium / Strong) ------------- */
  const getScore = (pwd) => {
    if (!pwd) return 0;
    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    return score; // 0-4
  };

  const score = getScore(password);
  const level = score <= 1 ? 1 : score <= 3 ? 2 : 3; // bars filled
  const levelLabel = level === 1 ? "Weak" : level === 2 ? "Medium" : "Strong";

  /* ------------- Validation ------------- */
  const validate = () => {
    const errs = {};
    if (!password) {
      errs.password = "New password is required.";
    } else if (password.length < 8) {
      errs.password = "Password must be at least 8 characters.";
    }
    if (!confirmPassword) {
      errs.confirmPassword = "Please confirm your new password.";
    } else if (password !== confirmPassword) {
      errs.confirmPassword = "Passwords do not match.";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    handleReset && handleReset(password, confirmPassword);
  };

  return (
    <div className="rp-page">
      <div className="rp-wrapper">
        {/* ================= LEFT PANEL ================= */}
        <div className="rp-left">
          <img src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTZZzXmtHDvrpG5V28qnmGJV3g1-WDEKzVz9zqHHpXfcZl0mcH40S6mmBE&s=10" 
          alt="Estate Experts Property" className="rp-left-image" />
          <div className="rp-left-overlay" />

          <div className="rp-brand">
            <img src={logo} alt="Estate Experts" className="rp-brand-logo" />
            <div className="rp-brand-text-wrap">
              <span className="rp-brand-text">Estate Experts</span>
              <span className="rp-brand-tagline">Real Estate Made Simple</span>
            </div>
          </div>

          <div className="rp-left-content">
            <h2 className="rp-left-heading">
              Reset Your
              <strong>Password</strong>
            </h2>
            <p className="rp-left-text">
              Get back to your account and continue your real estate journey
              with us.
            </p>
          </div>
        </div>

        {/* ================= RIGHT PANEL ================= */}
        <div className="rp-right">
          

          <div className="rp-form-area">
            {!success ? (
              <>
                <h1 className="rp-heading">Create New Password</h1>
                <p className="rp-subtext">
                  Enter your new password below to reset your{" "}
                  {userType === "seller" ? "seller" : "buyer"} account password
                  {email && (
                    <>
                      {" "}for <span className="rp-email-chip">{email}</span>
                    </>
                  )}
                  .
                </p>

                <form className="rp-form" onSubmit={onSubmit} noValidate>
                  {/* New Password */}
                  <div className="rp-field">
                    <label className="rp-label" htmlFor="rp-password">
                      New Password
                    </label>
                    <div className="rp-input-wrap">
                      <input
                        id="rp-password"
                        type={showPassword ? "text" : "password"}
                        className={`rp-input ${errors.password ? "rp-input-error" : ""}`}
                        placeholder="Enter new password"
                        autoComplete="new-password"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          if (errors.password) setErrors((p) => ({ ...p, password: "" }));
                        }}
                      />
                      <button
                        type="button"
                        className="rp-eye-btn"
                        onClick={() => setShowPassword((s) => !s)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <FaEye /> : <FaEyeSlash />}
                      </button>
                    </div>
                    {errors.password && <p className="rp-error-text">{errors.password}</p>}

                    {password && (
                      <div className="rp-strength">
                        <div className="rp-strength-row">
                          {[1, 2, 3].map((i) => (
                            <span
                              key={i}
                              className={`rp-strength-bar ${
                                i <= level
                                  ? `rp-strength-on ${level === 3 ? "rp-strength-strong" : ""}`
                                  : ""
                              }`}
                            />
                          ))}
                        </div>
                        <p className="rp-strength-label">
                          Password strength: <strong>{levelLabel}</strong>
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div className="rp-field">
                    <label className="rp-label" htmlFor="rp-confirm-password">
                      Confirm New Password
                    </label>
                    <div className="rp-input-wrap">
                      <input
                        id="rp-confirm-password"
                        type={showConfirm ? "text" : "password"}
                        className={`rp-input ${errors.confirmPassword ? "rp-input-error" : ""}`}
                        placeholder="Confirm new password"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          if (errors.confirmPassword)
                            setErrors((p) => ({ ...p, confirmPassword: "" }));
                        }}
                      />
                      <button
                        type="button"
                        className="rp-eye-btn"
                        onClick={() => setShowConfirm((s) => !s)}
                        aria-label={showConfirm ? "Hide password" : "Show password"}
                      >
                        {showConfirm ? <FaEye /> : <FaEyeSlash />}
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <p className="rp-error-text">{errors.confirmPassword}</p>
                    )}
                  </div>

                  <button type="submit" className="rp-submit-btn" disabled={loading}>
                    {loading ? (
                      <FaSpinner className="rp-spinner" />
                    ) : (
                      <>
                        <span>Reset Password</span>
                        <FaArrowRight />
                      </>
                    )}
                  </button>
                </form>

                <p className="rp-remember">
                  Remember your password?
                  <a href={loginPath}>Back to Login</a>
                </p>
              </>
            ) : (
              <div className="rp-success">
                <div className="rp-success-icon">
                  <FaCheckCircle />
                </div>
                <h2 className="rp-success-heading">Password Reset!</h2>
                <p className="rp-success-text">
                  Your password has been changed successfully. You can now log
                  in with your new password.
                </p>
                <a href={loginPath} className="rp-submit-btn">
                  <span>Go to Login</span>
                  <FaArrowRight />
                </a>
              </div>
            )}
          </div>

          <div className="rp-secure">
            <span className="rp-secure-inner">
              <FaShieldAlt /> Your information is safe with us.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;